import { useAppLoading } from "contexts";
import { FC, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DashboardFilterFormStyled,
  DatePickerStyled,
  DateRangePickerStyled,
  FormStyled,
  SelectStyled,
} from "./DashboardFilterForm.styled";
import { Button, Col, DatePickerProps, Form, Row } from "antd";
import statisticApi, {
  TFilterDashboardRequest,
} from "features/statistic/statisticApi";
import { DownOutlined } from "@ant-design/icons";
import shumokuApi from "features/shumoku/shumokuApi";
import { useHomePageContext } from "pages/HomePage/contexts";
import { TStore } from "model/shumoku";
import { FILTER_TRANSLATE } from "./DashboardFilterForm.translate";
import dayjs, { ConfigType } from "dayjs";
import {
  ADULT_SCHOOL_SURVEY,
  DATE_FORMAT_API,
  DATE_TIME_FORMAT,
  DatePickerType,
  GYM_SURVEY,
  JUNIOR_SCHOOL_SURVEY,
  MONTH_FORMAT,
  WEEK_FORMAT,
} from "constants/common";
import isoWeek from "dayjs/plugin/isoWeek";
import { ExportSurveyButton } from "../ExportSurveyButton/ExportSurveyButton";
import { STATISTIC_HOME_TRANSLATE } from "../StatisticHomePage/StatisticHomePage.translate";
import { ESurveyCategory } from "model/survey";
import { showErrorNotification } from "helpers/notification";
import MESSAGES from "constants/messages";

const COL_SPAN_LIST = {
  DATE_PICKER: {
    xs: 24,
    sm: 12,
  },
  STORE: {
    xs: 12,
    lg: 6,
  },
  GENDER: {
    xs: 6,
    lg: 6,
  },
  SURVEY_CATEGORY: {
    xs: 6,
    lg: 6,
  },
  AGE: {
    xs: 6,
    lg: 6,
  },
  TYPE_DATE: {
    xs: 8,
    lg: 8,
  },
  DATE: {
    xs: 8,
    lg: 8,
  },
  FILTER_BUTTON: {
    xs: 4,
    lg: 4,
  },
  EXPORT_BUTTON: {
    xs: 4,
    lg: 4,
  },
};

const initialValuesFormFilter: TFilterDashboardRequest = {
  fromDate: "",
  toDate: "",
  storeNumber: [""],
  gender: "",
  surveyCategory: 0,
  age: [""],
};

const SEARCH_FIELDS = {
  fromDate: "fromDate",
  toDate: "toDate",
  storeNumber: "storeNumber",
  gender: "gender",
  surveyCategory: "surveyCategory",
  age: "age",
  typeDate: "typeDate",
  date: "date",
  time: "time",
};

const genderOptions = [
  { value: "男性", label: "男性" },
  { value: "女性", label: "女性" },
];

const surveyTypeOptions = [
  { value: ESurveyCategory.GYM, label: GYM_SURVEY },
  { value: ESurveyCategory.ADULT_SCHOOL, label: ADULT_SCHOOL_SURVEY },
  { value: ESurveyCategory.JUNIOR_SCHOOL, label: JUNIOR_SCHOOL_SURVEY },
];

const eraOptions = [
  { value: "10代", label: "10代" },
  { value: "20代", label: "20代" },
  { value: "30代", label: "30代" },
  { value: "40代", label: "40代" },
  { value: "50代", label: "50代" },
  { value: "60代", label: "60代" },
  { value: "70代", label: "70代" },
  { value: "80代", label: "80代" },
];

const gradeOptions = [
  { value: "幼稚園・保育園", label: "幼稚園・保育園" },
  { value: "1年生", label: "1年生" },
  { value: "2年生", label: "2年生" },
  { value: "3年生", label: "3年生" },
  { value: "4年生", label: "4年生" },
  { value: "5年生", label: "5年生" },
  { value: "6年生", label: "6年生" },
  { value: "中学生以上", label: "中学生以上" },
];

const typeDateOptions = [
  { value: "date", label: `${FILTER_TRANSLATE.DATE_PICKER}` },
  { value: "week", label: `${FILTER_TRANSLATE.WEEK_PICKER}` },
  { value: "month", label: `${FILTER_TRANSLATE.MONTH_PICKER}` },
];

interface ISelectOption {
  value: string | number;
  label: string;
}

type PickerType = "week" | "month" | "date" | undefined;

const CLASS_NAME_SELECT_BEHIND_HEADER = "common__select--z-index-behind-header";
dayjs.extend(isoWeek);

type TProps = {
  activeTabKey: string;
};

const DashboardFilterForm: FC<TProps> = (props) => {
  const { activeTabKey } = props;
  const [formFilter] = Form.useForm();
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [isShowSurveyTypeFilter, setIsShowSurveyTypeFilter] = useState(false);
  const [storeOptions, setStoreOptions] = useState<ISelectOption[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { startLoading, stopLoading } = useAppLoading();
  const [typeDate, setTypeDate] = useState<PickerType>();
  const {
    setFilter,
    setChartQuestionIds,
    headquarterAdminData: { selectedSurveyType, setSelectedSurveyType },
  } = useHomePageContext();

  const handleSurveyTypeChange = async (value: unknown) => {
    setSelectedSurveyType(value as number);
    formFilter.setFieldsValue({
      age: [""],
    });
    try {
      const response = await statisticApi.getListChartQuestions(
        value as ESurveyCategory
      );
      setChartQuestionIds(response.data.map((question) => question.id));
    } catch (error) {
      showErrorNotification(MESSAGES["COM-MSG-002"]);
    }
  };

  const ageOptions =
    selectedSurveyType === ESurveyCategory.JUNIOR_SCHOOL
      ? [...gradeOptions]
      : [...eraOptions];

  useEffect(() => {
    setIsShowSurveyTypeFilter(
      activeTabKey === STATISTIC_HOME_TRANSLATE.SURVEY_TAB
    );
    if (activeTabKey === STATISTIC_HOME_TRANSLATE.DASHBOARD_TAB) {
      setSelectedSurveyType(ESurveyCategory.GYM);
    }
    if (activeTabKey === STATISTIC_HOME_TRANSLATE.SURVEY_TAB) {
      const surveyCategoryValue = formFilter.getFieldValue(
        SEARCH_FIELDS.surveyCategory
      );
      setSelectedSurveyType(surveyCategoryValue || ESurveyCategory.GYM);
    }
    formFilter.setFieldsValue({
      age: [""],
    });
  }, [activeTabKey]);

  const customWeekFormat: DatePickerProps["format"] = (value) => {
    return dayjs(value)
      .startOf(DatePickerType.Week)
      .add(1, DatePickerType.Week)
      .format(WEEK_FORMAT);
  };

  const fetchStoresData = async () => {
    try {
      startLoading;
      setIsLoadingStores(true);
      const storeList = await shumokuApi.getAllStoresWithoutFilter();
      if (storeList) {
        const storeOption = storeList.data.map((store: TStore) => {
          return {
            value: store.storeNumber,
            label: `${store.storeNumber}: ${store.storeName}`,
          };
        });
        setStoreOptions(storeOption);
      }
    } catch (error) {
      console.log(error);
    } finally {
      stopLoading();
      setIsLoadingStores(false);
    }
  };

  const getSearchParamValues = useCallback(() => {
    // set default value if not present
    const storeNumber =
      searchParams.getAll(SEARCH_FIELDS.storeNumber).length > 0
        ? searchParams.getAll(SEARCH_FIELDS.storeNumber)
        : [""];
    const age =
      searchParams.getAll(SEARCH_FIELDS.age).length > 0
        ? searchParams.getAll(SEARCH_FIELDS.age)
        : [""];
    const gender = searchParams.get(SEARCH_FIELDS.gender) || "";
    const fromDate = searchParams.get(SEARCH_FIELDS.fromDate) || "";
    const toDate = searchParams.get(SEARCH_FIELDS.toDate) || "";
    const surveyCategory =
      Number(searchParams.get(SEARCH_FIELDS.surveyCategory)) ||
      ESurveyCategory.GYM;

    const filterValues: TFilterDashboardRequest = {
      fromDate,
      toDate,
      storeNumber,
      surveyCategory,
      gender,
      age,
    };
    return filterValues;
  }, [searchParams]);

  const updateFormValueFirstTime = () => {
    const formValues = getSearchParamValues();
    formFilter.setFieldsValue(formValues);
  };

  const filterChart = useCallback(() => {
    const formValues = getSearchParamValues();
    // clear empty string of storeNumber and age
    if (
      formValues.storeNumber.length === 1 &&
      formValues.storeNumber[0] === ""
    ) {
      formValues.storeNumber = [];
    }

    if (formValues.age.length === 1 && formValues.age[0] === "") {
      formValues.age = [];
    }
    setFilter(formValues);
  }, [getSearchParamValues]);

  useEffect(() => {
    setSearchParams({}, { replace: false });
    fetchStoresData();
    updateFormValueFirstTime();
    formFilter.resetFields();
  }, []);

  const handleChangeStoreNumber = (values: any) => {
    // if select all then clear other option
    if (values[values.length - 1] === "" || values.length === 0) {
      formFilter.setFieldsValue({
        storeNumber: [""],
      });
    } else {
      const filteredValues = values.filter((value: string) => value !== "");
      formFilter.setFieldsValue({
        storeNumber: filteredValues,
      });
    }
  };
  const handleChangeAge = (values: any) => {
    // if select all then clear other option
    if (values[values.length - 1] === "" || values.length === 0) {
      formFilter.setFieldsValue({
        age: [""],
      });
    } else {
      const filteredValues = values.filter((value: string) => value !== "");
      formFilter.setFieldsValue({
        age: filteredValues,
      });
    }
  };

  const handleChangeTypeDate = (values: any) => {
    formFilter.setFieldsValue({
      date: "",
    });
    formFilter.setFieldsValue({
      time: "",
    });
    setTypeDate(values);
  };

  const updateFilterSearchParams = (values: TFilterDashboardRequest) => {
    const storeSelected =
      values.storeNumber.length > 0
        ? values.storeNumber.map((stt) => stt.toString())
        : [];
    const ageSelected =
      values.age.length > 0 ? values.age.map((stt) => stt.toString()) : [];
    setSearchParams(
      (prevSearchParams) => {
        const updatePrevParams = (filedName: string, value?: string) => {
          prevSearchParams.set(filedName, value || "");
        };

        if (storeSelected.length > 0) {
          prevSearchParams.delete(SEARCH_FIELDS.storeNumber);
          storeSelected.forEach((stt) => {
            prevSearchParams.append(SEARCH_FIELDS.storeNumber, stt);
          });
        }
        if (ageSelected.length > 0) {
          prevSearchParams.delete(SEARCH_FIELDS.age);
          ageSelected.forEach((stt) => {
            prevSearchParams.append(SEARCH_FIELDS.age, stt);
          });
        }

        updatePrevParams(SEARCH_FIELDS.gender, values.gender);

        if (values.date && typeDate) {
          if (typeDate === "date") {
            const [startDate, endDate] = values.date;
            const fromDateFormat = dayjs(startDate).format(DATE_TIME_FORMAT);
            const toDateFormat = dayjs(endDate).format(DATE_TIME_FORMAT);
            updatePrevParams(SEARCH_FIELDS.fromDate, fromDateFormat);
            updatePrevParams(SEARCH_FIELDS.toDate, toDateFormat);
          } else {
            const currentDate = dayjs();
            const fromDateFormat = dayjs(values.date)
              .startOf(typeDate)
              .format(DATE_FORMAT_API);
            updatePrevParams(SEARCH_FIELDS.fromDate, fromDateFormat);

            let toDateFormat = dayjs(values.date)
              .endOf(typeDate)
              .format(DATE_FORMAT_API);

            // if toDateFormat after current date then toDateFormat = current date
            if (dayjs(toDateFormat).isAfter(currentDate)) {
              toDateFormat = currentDate.format(DATE_FORMAT_API);
            }
            updatePrevParams(SEARCH_FIELDS.toDate, toDateFormat);
          }
        } else {
          updatePrevParams(SEARCH_FIELDS.fromDate, "");
          updatePrevParams(SEARCH_FIELDS.toDate, "");
        }

        return prevSearchParams;
      },
      { replace: true }
    );
    filterChart();
  };
  const disabledFutureDate = (current: ConfigType) => {
    if (typeDate) {
      const today = dayjs();
      const currentDay = dayjs(current);
      // disable future week
      if (currentDay.isAfter(today.endOf(typeDate))) {
        return true;
      }
      // disable future date in current week
      if (
        currentDay.isSame(today, typeDate) &&
        currentDay.isAfter(today, "day")
      ) {
        return true;
      }
    }
    return false;
  };

  return (
    <DashboardFilterFormStyled>
      <FormStyled
        colon={false}
        onFinish={updateFilterSearchParams}
        form={formFilter}
        initialValues={initialValuesFormFilter}
        layout="vertical"
      >
        <Row gutter={[8, 0]} wrap>
          <Col {...COL_SPAN_LIST.STORE}>
            <Form.Item
              label={FILTER_TRANSLATE.STORE}
              name={SEARCH_FIELDS.storeNumber}
            >
              <SelectStyled
                mode="multiple"
                options={[
                  { label: FILTER_TRANSLATE.ALL, value: "" },
                  ...storeOptions,
                ]}
                loading={isLoadingStores}
                disabled={isLoadingStores}
                notFoundContent={<div />}
                filterOption={(input, option) =>
                  (option?.label.toLowerCase() ?? "").includes(
                    input.toLowerCase()
                  )
                }
                suffixIcon={<DownOutlined />}
                showSearch
                popupClassName={CLASS_NAME_SELECT_BEHIND_HEADER}
                onChange={handleChangeStoreNumber}
                maxTagCount={"responsive"}
              />
            </Form.Item>
          </Col>
          <Col {...COL_SPAN_LIST.GENDER}>
            <Form.Item
              label={FILTER_TRANSLATE.GENDER}
              name={SEARCH_FIELDS.gender}
            >
              <SelectStyled
                options={[
                  { label: FILTER_TRANSLATE.ALL, value: "" },
                  ...genderOptions,
                ]}
                notFoundContent={<div />}
                filterOption={(input, option) =>
                  (option?.label ?? "").includes(input)
                }
                suffixIcon={<DownOutlined />}
                showSearch
                popupClassName={CLASS_NAME_SELECT_BEHIND_HEADER}
              />
            </Form.Item>
          </Col>
          <Col {...COL_SPAN_LIST.SURVEY_CATEGORY}>
            <Form.Item
              label={FILTER_TRANSLATE.SURVEY_CATEGORY}
              name={SEARCH_FIELDS.surveyCategory}
            >
              <SelectStyled
                onChange={handleSurveyTypeChange}
                disabled={!isShowSurveyTypeFilter}
                defaultValue={GYM_SURVEY}
                options={[...surveyTypeOptions]}
                notFoundContent={<div />}
                filterOption={(input, option) =>
                  (option?.label ?? "").includes(input)
                }
                suffixIcon={<DownOutlined />}
                showSearch
                popupClassName={CLASS_NAME_SELECT_BEHIND_HEADER}
              />
            </Form.Item>
          </Col>
          <Col {...COL_SPAN_LIST.AGE}>
            <Form.Item label={FILTER_TRANSLATE.AGE} name={SEARCH_FIELDS.age}>
              <SelectStyled
                mode="multiple"
                options={[
                  { label: FILTER_TRANSLATE.ALL, value: "" },
                  ...ageOptions,
                ]}
                notFoundContent={<div />}
                filterOption={(input, option) =>
                  (option?.label ?? "").includes(input)
                }
                suffixIcon={<DownOutlined />}
                showSearch
                popupClassName={CLASS_NAME_SELECT_BEHIND_HEADER}
                onChange={handleChangeAge}
                maxTagCount={"responsive"}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[8, 0]} wrap>
          <Col {...COL_SPAN_LIST.TYPE_DATE}>
            <Form.Item
              label={FILTER_TRANSLATE.TYPE_DATE}
              name={SEARCH_FIELDS.typeDate}
            >
              <SelectStyled
                options={[...typeDateOptions]}
                filterOption={(input, option) =>
                  (option?.label ?? "").includes(input)
                }
                suffixIcon={<DownOutlined />}
                showSearch
                popupClassName={CLASS_NAME_SELECT_BEHIND_HEADER}
                onChange={handleChangeTypeDate}
                placeholder={FILTER_TRANSLATE.SELECT_TYPE_DATE}
              />
            </Form.Item>
          </Col>
          {typeDate && typeDate === DatePickerType.Date ? (
            <>
              <Col {...COL_SPAN_LIST.DATE}>
                <Form.Item
                  label={FILTER_TRANSLATE.PERIOD}
                  name={SEARCH_FIELDS.date}
                >
                  <DateRangePickerStyled
                    picker={typeDate}
                    disabled={!typeDate}
                    format="YYYY-MM-DD HH:mm"
                    showTime
                    disabledDate={disabledFutureDate}
                  />
                </Form.Item>
              </Col>
            </>
          ) : (
            <>
              <Col {...COL_SPAN_LIST.DATE}>
                <Form.Item
                  label={FILTER_TRANSLATE.PERIOD}
                  name={SEARCH_FIELDS.date}
                >
                  <DatePickerStyled
                    picker={typeDate}
                    disabledDate={disabledFutureDate}
                    disabled={!typeDate}
                    format={
                      typeDate === DatePickerType.Week
                        ? customWeekFormat
                        : MONTH_FORMAT
                    }
                  />
                </Form.Item>
              </Col>
            </>
          )}
          <Col {...COL_SPAN_LIST.FILTER_BUTTON} className="filter-submit">
            <Button type="primary" htmlType="submit" className="btn-filter">
              {FILTER_TRANSLATE.FILTER}
            </Button>
          </Col>
          <Col {...COL_SPAN_LIST.EXPORT_BUTTON}>
            <ExportSurveyButton storeOptions={storeOptions} />
          </Col>
        </Row>
      </FormStyled>
    </DashboardFilterFormStyled>
  );
};

export { DashboardFilterForm };
